// Delivery type options for job creation
export const DELIVERY_TYPES = [
  { value: 'houses', label: 'Houses', icon: '🏠', zoneType: 'polygon', needsGrid: true, autoCount: true },
  { value: 'apartments', label: 'Apartments', icon: '🏢', zoneType: 'polygon', needsGrid: true, autoCount: true },
  { value: 'address_specific', label: 'Address Specific', icon: '📍', zoneType: 'points', needsGrid: false, autoCount: false },
  { value: 'canvassing', label: 'Canvassing', icon: '🗳️', zoneType: 'points', needsGrid: false, autoCount: false },
  { value: 'cars', label: 'Cars', icon: '🚗', zoneType: 'circle', needsGrid: false, autoCount: false },
  { value: 'hands', label: 'Hands (Direct)', icon: '🤝', zoneType: 'circle', needsGrid: false, autoCount: false },
];

// Constants for job value calculations
export const MIN_JOB_VALUE = 100;
export const MAX_APARTMENTS_PER_GRID = 1000;
export const DEFAULT_RADIUS_MILES = 1;
export const APARTMENT_CIRCLE_RADIUS_MILES = 0.05;

// Calculate max per grid - FIXED at 500 houses per grid
export const calculateMaxPerGrid = (payPerFlyer) => {
  // Fixed at 500 houses per grid, regardless of pay rate
  // Leftover houses in final grid are acceptable
  return 500;
};

// Calculate center of points
export const calculateCenter = (points) => {
  if (!points || points.length === 0) return null;
  const lat = points.reduce((sum, p) => sum + p[0], 0) / points.length;
  const lng = points.reduce((sum, p) => sum + p[1], 0) / points.length;
  return [lat, lng];
};

// Calculate polygon area in square meters using the Shoelace formula with geodesic correction
export const calculatePolygonAreaSqMeters = (points) => {
  if (points.length < 3) return 0;
  
  // Use the Shoelace formula with proper lat/lng to meters conversion
  const R = 6371000; // Earth's radius in meters
  let area = 0;
  
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const lat1 = points[i][0] * Math.PI / 180;
    const lat2 = points[j][0] * Math.PI / 180;
    const lng1 = points[i][1] * Math.PI / 180;
    const lng2 = points[j][1] * Math.PI / 180;
    
    area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  
  area = Math.abs(area * R * R / 2);
  return area;
};

// Calculate polygon area in acres
export const calculatePolygonAreaAcres = (points) => {
  const sqMeters = calculatePolygonAreaSqMeters(points);
  return sqMeters / 4046.86; // 1 acre = 4046.86 sq meters
};

// Housing density estimates by area type (houses per acre)
// Based on US Census data and urban planning standards
export const HOUSING_DENSITY = {
  // Suburban residential: 2-5 houses per acre (average 3.5)
  suburban: 3.5,
  // Urban residential: 5-10 houses per acre (average 7)
  urban: 7,
  // Dense urban: 10-20 houses per acre (average 12)
  dense_urban: 12,
  // Rural: 0.5-2 houses per acre (average 1)
  rural: 1,
  // Default for unknown areas
  default: 4
};

// Estimate houses from polygon area using accurate geodesic calculation
// This provides a CONSISTENT estimate based on area
export const estimateHousesFromArea = (points, densityType = 'default') => {
  if (points.length < 3) return 0;
  
  const areaAcres = calculatePolygonAreaAcres(points);
  const density = HOUSING_DENSITY[densityType] || HOUSING_DENSITY.default;
  
  // Apply a conservative multiplier (0.7) to account for roads, parks, commercial areas
  const adjustedDensity = density * 0.7;
  
  return Math.round(areaAcres * adjustedDensity);
};

// Deterministic hash function for polygon to ensure consistent caching
export const hashPolygon = (polygon) => {
  if (!polygon || polygon.length === 0) return '';
  // Round coordinates to 5 decimal places for consistency
  const rounded = polygon.map(p => [
    Math.round(p[0] * 100000) / 100000,
    Math.round(p[1] * 100000) / 100000
  ]);
  return JSON.stringify(rounded);
};

// Get combined bounding box for multiple polygons
export const getCombinedBoundingBox = (polys) => {
  const allPoints = polys.flat();
  if (allPoints.length === 0) return [];
  
  const lats = allPoints.map(p => p[0]);
  const lngs = allPoints.map(p => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  
  return [
    [minLat, minLng],
    [minLat, maxLng],
    [maxLat, maxLng],
    [maxLat, minLng]
  ];
};

// Point in polygon check
export const pointInPolygon = (point, polygon) => {
  const x = point[0], y = point[1];
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

// Convert a (center, radius) circle to a polygon ring of N vertices using
// the spherical "destination given distance + bearing" formula. Used by the
// Radius Draw zone tool — the downstream zone-counting / push-to-county /
// grid pipelines all operate on polygons, so we approximate the circle as a
// regular 64-gon (~99.7% area match to a true circle, indistinguishable on
// any city-scale map). For circles smaller than ~50 mi the spherical math
// is essentially exact at typical zoom levels.
export const circleToPolygon = (centerLatLng, radiusMeters, numPoints = 64) => {
  if (!centerLatLng || !(radiusMeters > 0)) return [];
  const [centerLat, centerLng] = centerLatLng;
  const R = 6378137; // Earth radius in meters (WGS-84)
  const angDist = radiusMeters / R;
  const lat1 = (centerLat * Math.PI) / 180;
  const lng1 = (centerLng * Math.PI) / 180;
  const points = [];
  for (let i = 0; i < numPoints; i += 1) {
    const bearing = (i / numPoints) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(angDist) +
        Math.cos(lat1) * Math.sin(angDist) * Math.cos(bearing),
    );
    const lng2 = lng1 + Math.atan2(
      Math.sin(bearing) * Math.sin(angDist) * Math.cos(lat1),
      Math.cos(angDist) - Math.sin(lat1) * Math.sin(lat2),
    );
    points.push([(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI]);
  }
  return points;
};

// Convenience: meters → miles, rounded to the precision useful for a live
// drag-the-radius display (2 decimals so the number visibly grows as the
// user drags even on small radii).
export const metersToMilesRounded = (meters, decimals = 2) => {
  if (!(meters > 0)) return 0;
  const miles = meters / 1609.344;
  const factor = 10 ** decimals;
  return Math.round(miles * factor) / factor;
};
